import { z } from 'zod';
/**
 * Request + response shape for `POST /api/onboarding/enrich-company`.
 *
 * The endpoint takes a URL (and optional pre-typed name), runs a
 * Google-Search-grounded Gemini call, and returns structured public-
 * record info (stage, headcount, industry) so the agent-led onboarding
 * wizard can pre-fill fields with TRUTHFUL guesses instead of the
 * hard-coded mock the page used to ship with ("Series A / 28 /
 * Vertical AI" for every URL).
 *
 * Each output field carries an explicit `confidence`. The wizard
 * currently uses `confidence !== 'unknown'` as the threshold for
 * surfacing a value (see `pickInferred()` in `onboarding-agent-led.tsx`)
 * and renders an `INFERRED` label next to the field plus an "Inferred
 * from {url}" footer line. The richer per-field citation tooltip + the
 * right-rail `summary` panel described by an earlier draft of this
 * shape aren't wired yet — those need follow-up UI work; the schema
 * is forward-compatible so the wire shape doesn't need to change.
 *
 * No Firestore involved — this is purely a read-from-the-web shape.
 * The wizard ultimately writes the user-confirmed values into the
 * `businessContexts/<orgId>` doc via the existing
 * `PUT /orgs/<orgId>/business-context` endpoint.
 */
export const Confidence = z.enum(['high', 'medium', 'low', 'unknown']);
export const CompanyStage = z.enum([
    'Pre-seed',
    'Seed',
    'Series A',
    'Series B',
    'Series C+',
    'Bootstrapped',
    'Public',
    'Unknown',
]);
const EnrichedField = (value) => z.object({
    value,
    confidence: Confidence,
});
export const EnrichCompanyRequest = z.object({
    /** Public homepage URL of the company being onboarded. */
    url: z.string().url(),
    /** Optional — user-typed company name; passed to Gemini as a hint. */
    companyName: z.string().min(1).max(200).optional(),
});
export const EnrichCompanyResponse = z.object({
    /** Echoes the input URL so the UI can decide whether to keep the row
     *  (e.g. if the user changes the URL mid-flight). */
    url: z.string().url(),
    /** Resolved company name. May come from the user-typed hint or be
     *  inferred from the homepage `<title>` / About page. */
    name: EnrichedField(z.string().max(200)).nullable(),
    stage: EnrichedField(CompanyStage).nullable(),
    /** Approximate headcount as a stringified range or count
     *  (`"28"`, `"~50"`, `"50–100"`). Free-form because public sources
     *  rarely agree on an exact number. */
    headcount: EnrichedField(z.string().max(50)).nullable(),
    /** Short categorical industry label (`"B2B SaaS"`,
     *  `"Consumer fintech"`, `"Vertical AI for healthcare"`). */
    industry: EnrichedField(z.string().max(120)).nullable(),
    /** Free-text "what they do" — one line, populated by the model from
     *  homepage + about copy. Feeds the BusinessContext.companyDescription. */
    description: EnrichedField(z.string().max(400)).nullable(),
    /** Free-text "who they serve" — one line. Feeds customerDescription. */
    customer: EnrichedField(z.string().max(400)).nullable(),
    /** 1-4 short industry tags ("infra", "developer-tools", "ai"). */
    industryTags: EnrichedField(z.array(z.string().max(40)).max(6)).nullable(),
    /** Region keys ('us-bayarea', 'eu-london', etc.) — model picks from the
     *  same closed set the wizard's region chips use. */
    region: EnrichedField(z.array(z.string()).max(6)).nullable(),
    /** A north-star metric guess based on the industry + business model. */
    suggestedNorthStarMetric: EnrichedField(z.string().max(120)).nullable(),
    /** 3-5 plausible quarterly priorities derived from the company's stage
     *  + signals on their homepage (hiring/blog/news). */
    suggestedPriorities: EnrichedField(z.array(z.string().max(200)).max(6)).nullable(),
    /** Signal-topic keys we'd subscribe by default for this org. */
    suggestedTopics: EnrichedField(z.array(z.string()).max(12)).nullable(),
    /** "Current focus this quarter" guess based on recent press/blog signals. */
    suggestedCurrentFocus: EnrichedField(z.string().max(200)).nullable(),
    /** URLs the model grounded against. Available for a future per-field
     *  citation tooltip / "view sources" affordance — not yet rendered
     *  by the v5 wizard. */
    citations: z.array(z.object({
        url: z.string().url(),
        title: z.string().max(300).optional(),
    })),
    /** Free-text summary of what the model concluded. Available for a
     *  future right-rail explainer panel — not yet rendered by the v5
     *  wizard (the only thing it surfaces today is the "Inferred from
     *  {url}" footer). */
    summary: z.string().max(2000),
});
